import { useEffect, useRef, useState } from "react";

/** `M:SS`, matching the timer bar. */
const mmss = (ms: number): string => {
  const total = Math.ceil(ms / 1000);
  return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, "0")}`;
};
import { RESULTS_EMOJI } from "@badorbad/shared";
import { AvatarFace } from "../design/AvatarFace";
import { nameColorFor } from "../design/seed";
import { Cue, play } from "../design/sound";
import { NAME_COLORS } from "../design/tokens";
import { t, fmt } from "../locale";
import { useGame, selectIsHost } from "../stores/game";
import { Reactions } from "./Reactions";

/**
 * Final scores. plan.md §1.8: the ranking with **ties sharing a position**, no
 * tie-break, no titles or awards, no share button — players screenshot.
 *
 * The host closes it, which returns the room to the lobby and reopens joining.
 * If nobody does, the server returns it anyway after five minutes — so a host
 * who walks away cannot hold the room until its TTL. See plan.md §5.13 for why
 * that runs always rather than only during maintenance, and why five minutes
 * rather than §1.10's two.
 */
export const Results = () => {
  const ranking = useGame((s) => s.ranking);
  const players = useGame((s) => s.players);
  const isHost = useGame(selectIsHost);
  const closeResults = useGame((s) => s.closeResults);
  const react = useGame((s) => s.react);
  const closesAt = useGame((s) => s.resultsDeadlineAt);

  // Counted down locally against the server's absolute instant, so a
  // backgrounded tab cannot drift and nothing is sent per second.
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!closesAt) return;
    const id = setInterval(() => setNow(Date.now()), 1_000);
    return () => clearInterval(id);
  }, [closesAt]);
  const remaining = closesAt
    ? Math.max(new Date(closesAt).getTime() - now, 0)
    : 0;
  const seconds = Math.ceil(remaining / 1000);
  const tickedSecond = useRef<number | null>(null);
  useEffect(() => {
    tickedSecond.current = null;
  }, [closesAt]);
  useEffect(() => {
    if (seconds < 1 || seconds > 10 || tickedSecond.current === seconds) return;
    tickedSecond.current = seconds;
    play(Cue.TimeLow);
  }, [seconds]);

  if (!ranking) return null;

  const scoreLabel = (score: number) =>
    score === 0
      ? t.results.noPoints
      : score === 1
        ? t.results.onePoint
        : fmt(t.results.points, String(score));

  return (
    <div className="relative mx-auto flex min-h-full max-w-md flex-col px-5 pb-6 pt-8">
      <Reactions />

      <h1 className="text-center text-2xl">{t.results.title}</h1>

      <ol className="mt-6 flex flex-1 flex-col gap-2">
        {ranking.map((entry) => {
          const who = players.find((p) => p.user_id === entry.user_id);
          // A player who left before the results still holds their score — the
          // game never ends early because people leave (§1.7).
          const seed = who?.avatar_seed ?? entry.user_id;
          return (
            <li
              key={entry.user_id}
              className="flex items-center gap-3 bg-surface px-3 py-2"
            >
              <span className="w-6 shrink-0 text-center text-lg font-bold tabular-nums text-ink-dim">
                {entry.position}
              </span>
              <AvatarFace seed={seed} size={40} />
              <span
                className="min-w-0 flex-1 truncate text-base font-bold"
                style={{ color: nameColorFor(seed, NAME_COLORS) }}
              >
                {who?.display_name ?? ""}
              </span>
              <span className="shrink-0 text-sm text-ink-dim">
                {scoreLabel(entry.score)}
              </span>
            </li>
          );
        })}
      </ol>

      {/* Both sets, mixed: the same screen is being read by whoever came first
          and whoever came last. */}
      <div className="mt-6 flex justify-center gap-2">
        {RESULTS_EMOJI.map((emoji) => (
          <button
            key={emoji}
            data-sound="reaction"
            aria-label={fmt(t.reveal.react, emoji)}
            className="flex h-12 w-12 select-none touch-manipulation items-center justify-center rounded-full text-2xl transition-transform active:scale-90"
            onClick={() => {
              react(emoji);
            }}
          >
            {emoji}
          </button>
        ))}
      </div>

      <div className="mt-4 flex flex-col gap-2">
        {isHost ? (
          <button
            className="w-full bg-side-a px-4 py-4 text-base font-bold uppercase tracking-tight text-bg"
            onClick={closeResults}
          >
            {t.results.backToLobby}
          </button>
        ) : (
          <p className="text-center text-sm text-ink-dim">
            {t.results.waitingForHost}
          </p>
        )}
        {closesAt && (
          <p className="text-center text-[11px] text-ink-dim">
            {fmt(t.results.autoClose, mmss(remaining))}
          </p>
        )}
      </div>
    </div>
  );
};
